import ReactGA from 'react-ga';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isWebComponentFlow } from './commonCode';
import { wcApiUrl } from '../WebComponents/commonComponents/customUtils';

const Analytics = (props) => {
  const baseContext = isWebComponentFlow();
  const { initiateUiResponse, setGoogleCID, environment, wcFlow } = baseContext;
  const { analyticsInitialized } = props;
  const gaInitializedRef = useRef(false);
  const gtmInitializedRef = useRef(false);
  const gaReadyRef = useRef(false);
  const isCancelledRef = useRef(false);

  const getGATrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GA_TRACKING_ID[environment]
      : initiateUiResponse?.REACT_APP_GA_TRACKING_ID;

  const getGTMTrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GTM_ID[environment]
      : initiateUiResponse?.REACT_APP_GTM_ID;

  const envCondition = wcFlow
    ? environment === 'dev' || environment === 'prod'
    : initiateUiResponse?.REACT_APP_ENVIRONMENT === 'production' ||
      initiateUiResponse?.REACT_APP_ENVIRONMENT === 'development';

  const initGA = (id) => {
    if (!id || gaInitializedRef.current) return;

    gaInitializedRef.current = true;
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script.onload = () => {
      window.gtag('js', new Date());
      window.gtag('config', id);
      gaReadyRef.current = true;
    };
    script.onerror = () => {
      gaInitializedRef.current = false;
      gaReadyRef.current = false;
    };
    document.head.appendChild(script);
  };

  const initGTM = ({ gtmId, dataLayerName = 'dataLayer' }) => {
    if (!gtmId || gtmInitializedRef.current) {
      return;
    }

    gtmInitializedRef.current = true;
    window[dataLayerName] = window[dataLayerName] || [];
    window[dataLayerName].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${
      dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
    }`;
    script.onerror = () => {
      gtmInitializedRef.current = false;
    };
    document.head.appendChild(script);
  };

  const captureCID = () => {
    let attempts = 0;
    const maxAttempts = 30;

    const checkCID = () => {
      if (isCancelledRef.current) return;

      attempts += 1;

      if (window?.ga?.getAll) {
        const trackers = window.ga.getAll();
        const tracker = trackers.find((t) => t.get('trackingId') === getGATrackingID());

        if (tracker) {
          const clientId = tracker.get('clientId');

          if (clientId) {
            setGoogleCID(clientId);
            return;
          }
        }
      }

      if (attempts < maxAttempts) {
        setTimeout(checkCID, 100);
      }
    };
    checkCID();
  };

  useEffect(() => {
    isCancelledRef.current = false;

    if (getGATrackingID() && !analyticsInitialized) {
      initGA(getGATrackingID());
    }
    if (envCondition && !analyticsInitialized) {
      // Replaced react-gtm-module to avoid its inline GTM bootstrap script, which violates CSP.
      initGTM({
        gtmId: getGTMTrackingID(),
      });

      const tryInitReactGA = () => {
        if (isCancelledRef.current) return;

        if (!gaReadyRef.current) {
          setTimeout(tryInitReactGA, 50);
          return;
        }
        ReactGA.initialize(getGATrackingID());
        ReactGA.pageview(window.location.pathname + window.location.search);
      };
      tryInitReactGA();
    }
    if (analyticsInitialized) {
      gaReadyRef.current = true;
    }

    const tryCaptureCID = () => {
      if (isCancelledRef.current) return;

      if (!gaReadyRef.current) {
        setTimeout(tryCaptureCID, 50);
        return;
      }
      captureCID();
    };
    tryCaptureCID();

    return () => {
      isCancelledRef.current = true;
    };
  }, [initiateUiResponse]);

  return null;
};

export default Analytics;

Analytics.propTypes = {
  analyticsInitialized: PropTypes.bool,
};
